import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Container, Card, CardContent, Typography, CircularProgress, Alert, Button, Stack } from '@mui/material';
import CheckCircle from '@mui/icons-material/CheckCircle';
import ErrorOutline from '@mui/icons-material/ErrorOutline';
import { apiService } from '../services/api';

type State = 'verifying' | 'approved' | 'pending' | 'failed';

export default function SubscriptionSuccess() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [state, setState] = useState<State>('verifying');
  const [message, setMessage] = useState<string>('');

  const collectionStatus = (params.get('collection_status') || params.get('status') || '').toLowerCase();

  useEffect(() => {
    if (['rejected', 'failure', 'cancelled'].includes(collectionStatus)) {
      setState('failed');
      setMessage('El pago no se completó. Podés reintentar desde tu panel.');
      return;
    }

    let attempts = 0;
    const maxAttempts = 8;
    const poll = async () => {
      try {
        const res = await (apiService as any).api.get('/checkout/status');
        const data = res.data;
        if (data.status === 'Active' && data.subscriptionActive) {
          setState('approved');
          setMessage('¡Tu suscripción está activa! Ya podés usar todas las features de tu plan.');
          return true;
        }
        return false;
      } catch {
        return false;
      }
    };

    const interval = setInterval(async () => {
      attempts += 1;
      const done = await poll();
      if (done || attempts >= maxAttempts) {
        clearInterval(interval);
        if (!done) {
          setState('pending');
          setMessage('Mercado Pago recibió el pago. La activación puede demorar unos minutos — vas a recibir un email cuando se confirme.');
        }
      }
    }, 3000);

    poll().then((done) => { if (done) clearInterval(interval); });

    return () => clearInterval(interval);
  }, [collectionStatus]);

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', bgcolor: '#f5f7fb', py: 6 }}>
      <Container maxWidth="sm">
        <Card>
          <CardContent sx={{ p: 5, textAlign: 'center' }}>
            <Box sx={{ mb: 3 }}>
              {state === 'approved' && <CheckCircle sx={{ fontSize: 80, color: 'success.main' }} />}
              {state === 'failed' && <ErrorOutline sx={{ fontSize: 80, color: 'error.main' }} />}
              {(state === 'verifying' || state === 'pending') && <CircularProgress size={64} />}
            </Box>
            <Typography variant="h4" fontWeight={700} gutterBottom>
              {state === 'approved' && '¡Pago confirmado!'}
              {state === 'failed' && 'El pago no se completó'}
              {state === 'pending' && 'Pago recibido — activación pendiente'}
              {state === 'verifying' && 'Confirmando tu pago...'}
            </Typography>
            {message && (
              <Alert
                severity={state === 'failed' ? 'error' : state === 'approved' ? 'success' : 'info'}
                sx={{ mt: 2, mb: 3, textAlign: 'left' }}
              >
                {message}
              </Alert>
            )}
            <Stack direction="row" spacing={2} justifyContent="center" sx={{ mt: 2 }}>
              <Button variant="contained" onClick={() => navigate('/dashboard')}>
                Ir al panel
              </Button>
              {state === 'failed' && (
                <Button variant="outlined" onClick={() => navigate('/subscription/upgrade')}>
                  Reintentar
                </Button>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
}
